import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const projectTypes = [
  'Custom Design',
  'Rapid Prototyping',
  'Art Piece',
  'Functional Part',
  'Miniatures',
  'Personalized Gift',
  'Other',
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      await fetch('https://readdy.ai/api/form/d79f6vb2hsar0k5m0mqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      // still show success to user
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 md:px-10 bg-[#0f0f14] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/874769699e9bf31cdc16112b67fa9bcf.jpg"
            alt="Contact background"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f14]/50 to-[#0f0f14]"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <span className="text-xs uppercase tracking-[3px] font-bold bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
            Let&apos;s Talk
          </span>
          <h1 className="text-5xl md:text-6xl font-black text-white mt-4 leading-tight">
            Start Your<br />
            <span className="bg-gradient-to-r from-fuchsia-400 via-orange-300 to-cyan-400 bg-clip-text text-transparent">
              Project
            </span>
          </h1>
          <p className="text-white/60 text-lg mt-5 leading-relaxed">
            Have an idea? A file? Just a napkin sketch? We want to hear it. Fill out the form and we&apos;ll get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6 md:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16">

            {/* Left - Info */}
            <div className="lg:w-80 flex-shrink-0">
              <h2 className="text-2xl font-black text-gray-900 mb-6">Get In Touch</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-10">
                We&apos;re a small operation — which means you&apos;re talking directly to the people who will actually build your project. No middlemen, no runaround.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex-shrink-0">
                    <i className="ri-mail-line text-white text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">Email</p>
                    <a href="mailto:hello@readerbrothers.com" rel="nofollow" className="text-gray-800 text-sm font-medium hover:text-fuchsia-600 transition-colors">
                      hello@readerbrothers.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-400 to-yellow-400 flex-shrink-0">
                    <i className="ri-instagram-line text-white text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">Instagram</p>
                    <a href="#" rel="nofollow" className="text-gray-800 text-sm font-medium hover:text-fuchsia-600 transition-colors">
                      @readerbrothersprint
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 flex-shrink-0">
                    <i className="ri-time-line text-white text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">Response Time</p>
                    <p className="text-gray-800 text-sm font-medium">Within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* Quote */}
              <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-fuchsia-50 to-cyan-50 border border-fuchsia-100">
                <i className="ri-double-quotes-l text-3xl bg-gradient-to-r from-fuchsia-500 to-orange-400 bg-clip-text text-transparent"></i>
                <p className="text-gray-700 text-sm leading-relaxed mt-2 italic">
                  If you can think it, we can build it.
                </p>
                <p className="text-xs text-gray-400 mt-3 font-semibold">— Reader Brothers Printing</p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="flex-1">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-400 mb-6">
                    <i className="ri-check-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-3">Message Sent!</h3>
                  <p className="text-gray-500 text-base max-w-sm">
                    Thanks for reaching out! We&apos;ll review your project details and get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        placeholder="John"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-fuchsia-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        placeholder="Doe"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-fuchsia-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-fuchsia-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                      Project Type
                    </label>
                    <select
                      name="projectType"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-fuchsia-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select a project type...</option>
                      {projectTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                      Tell Us About Your Project *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Describe your idea, dimensions, materials, quantity, deadline — anything that helps us understand what you need..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-fuchsia-400 transition-colors resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || charCount > 500}
                    className="whitespace-nowrap w-full py-4 rounded-xl font-bold text-white text-base bg-gradient-to-r from-fuchsia-500 via-orange-400 to-cyan-400 hover:opacity-90 transition-opacity disabled:opacity-60 cursor-pointer"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
